import { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import Editor from "../components/blog-editor/Editor";
import toast from "react-hot-toast";
import axios from "../utils/axios";
import { getHaystackersBlogCategories } from "../api/haystackersBlogCategories";

const HaystackersBlogsDetails = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [blogData, setBlogData] = useState(null);
  const [blogStatus, setBlogStatus] = useState(null);
  const [isInCarousel, setIsInCarousel] = useState(false);
  const [categoryIds, setCategoryIds] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(
    async (content) => {
      try {
        setIsSaving(true);
        await axios.put(`/admin/haystackers-blogs/data/${id}`, {
          blogData: content,
          status: blogStatus,
          isInCarousel: isInCarousel === "true" ? true : false,
          categoryIds: categoryIds || [],
        });

        toast.success("Content saved successfully!");
      } catch (error) {
        console.error("Error saving content:", error);
        toast.error("Failed to save content");
      } finally {
        setIsSaving(false);
      }
    },
    [id, blogStatus, isInCarousel, categoryIds],
  );

  const handleLoad = useCallback(async () => {
    try {
      setIsLoading(true);

      const [blogResponse, categoriesResponse] = await Promise.all([
        axios.get(`/admin/haystackers-blogs/${id}`),
        getHaystackersBlogCategories(),
      ]);

      const blogInfo = blogResponse?.data?.data || blogResponse.data;
      setAllCategories(categoriesResponse?.data || []);
      const blogContent = blogInfo?.blogData || blogInfo?.content || null;

      setBlogData(blogInfo);
      setBlogStatus(blogInfo?.status || "DRAFT");
      setIsInCarousel(blogInfo?.isInCarousel || false);
      setCategoryIds(
        Array.isArray(blogInfo?.categories)
          ? blogInfo.categories.map((c) => c._id)
          : [],
      );

      if (blogContent && blogContent.blocks && blogContent.blocks.length > 0) {
        setData(blogContent);
      } else {
        setData(null);
      }

      toast.success("Content loaded successfully!");
      return blogContent;
    } catch (error) {
      console.error("Error loading content:", error);
      toast.error("Failed to load content");
      setData(null);
      setBlogData(null);
      setBlogStatus("DRAFT");
      setCategoryIds([]);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const handleClear = useCallback(() => {
    toast.success("Content cleared");
  }, []);

  const handleExport = useCallback((content) => {
    try {
      const exportData = {
        title: "Blog Content Export",
        timestamp: new Date().toISOString(),
        content: content,
        wordCount:
          content.blocks?.reduce((count, block) => {
            if (block.type === "paragraph" || block.type === "header") {
              return count + (block.data.text || "").split(" ").length;
            }
            return count;
          }, 0) || 0,
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `haystackers-blog-content-${
        new Date().toISOString().split("T")[0]
      }.json`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success("Content exported successfully!");
    } catch (error) {
      console.error("Error exporting content:", error);
      toast.error("Failed to export content");
    }
  }, []);

  useEffect(() => {
    if (id) {
      handleLoad();
    }
  }, [id, handleLoad]);

  return (
    <div className="w-full min-h-screen">
      <div className="w-full max-w-none">
        {isLoading ? (
          <div className="w-full px-0">
            <div className="flex items-center justify-center h-64">
              <div className="inline-flex items-center gap-2 text-gray-600">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                Loading blog content...
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full px-0">
            <Editor
              data={
                data || {
                  time: Date.now(),
                  blocks: [
                    {
                      id: "SzwhuyoFq6",
                      type: "paragraph",
                      data: {
                        text: "Enter your blog content here...",
                      },
                    },
                  ],
                }
              }
              setData={setData}
              onSave={handleSave}
              onLoad={handleLoad}
              onClear={handleClear}
              onExport={handleExport}
              showControls={true}
              readOnly={false}
              blogStatus={blogStatus}
              setBlogStatus={setBlogStatus}
              setIsInCarousel={setIsInCarousel}
              isInCarousel={isInCarousel}
              categoryIds={categoryIds}
              setCategoryIds={setCategoryIds}
              allCategories={allCategories}
              allData={blogData}
              backToListPath="/dashboard/haystackers-blogs"
            />
          </div>
        )}

        {isSaving && (
          <div className="mt-4 text-center px-6">
            <div className="inline-flex items-center gap-2 text-gray-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              Saving content...
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HaystackersBlogsDetails;
